<script>
import DispatcherSelect from "@/components/selects/DispatcherSelect.vue";
import FormBlock from "@/components/form/FormBlock.vue";
import OmniOmniTextarea from "@/components/omni/OmniTextarea.vue";
import OmniSelect from "@/components/selects/OmniSelect.vue";
import CustomerSelect from "@/views/orders/components/CustomerSelect.vue";
import OrderRefNumberInput from "@/views/orders/components/OrderRefNumberInput.vue";
import EmailsField from "@/components/inputs/EmailsField.vue";
import ValidationMixin from "@/mixins/validation.mixin.ts";
import { OrdersMixin } from "@/views/orders/mixins/orders.mixin.ts";
import OrderTemperatureRange from "@/views/orders/edit/components/OrderTemperatureRange.vue";
import OrderPayments from "@/components/order/OrderPayments.vue";
import { mapState } from "pinia";
import { useAppStore } from "@/store/app.store.ts";
import { Role } from "@/constants/navigation.ts";

export default {
  name: "OrderEditInfo",
  components: {
    OrderPayments,
    OrderTemperatureRange,
    EmailsField,
    OrderRefNumberInput,
    CustomerSelect,
    OmniSelect,
    OmniTextarea: OmniOmniTextarea,
    FormBlock,
    DispatcherSelect,
  },
  mixins: [ValidationMixin, OrdersMixin],
  props: {
    modelValue: Object,
  },
  emits: ["update:modelValue"],
  data() {
    return {
      billToCustomer: null,
      emails: [],
      autofilledEmails: [],
    };
  },
  computed: {
    Role() {
      return Role;
    },
    ...mapState(useAppStore, ["dispatcher", "getDispatcher"]),
    isCreateNew() {
      return this.$route.params.id === undefined;
    },
    order: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    temperatureFrom: {
      get() {
        return this.order.temperatures[0] || "";
      },
      set(value) {
        this.order.temperatures[0] = value;
      },
    },
    temperatureTo: {
      get() {
        return this.order.temperatures[1] || "";
      },
      set(value) {
        this.order.temperatures[1] = value;
      },
    },
    checkInAsList() {
      if (!this.dispatcher) {
        return [];
      }

      const items = [this.dispatcher.company.name];
      if (this.billToCustomer && this.billToCustomer.name) {
        items.push(this.billToCustomer.name);
      }
      return items;
    },
  },
  watch: {
    modelValue: {
      handler() {
        this.emails = this.order.sendUpdatesTo.map((email) => {
          return {
            value: email,
          };
        });
      },
    },
    "order.bookedBy"(dispatcherId) {
      if (!this.isCreateNew || !dispatcherId || !this.shouldAutofillSendUpdates()) {
        return;
      }

      const bookingDispatcherEmails = this.getDispatcherEmails(dispatcherId);

      if (bookingDispatcherEmails.length === 0) {
        return;
      }

      this.autofilledEmails = bookingDispatcherEmails;
      this.emails = bookingDispatcherEmails.map((email) => ({ value: email }));
      this.updateOrderSendUpdatesTo(this.emails);
    },
  },
  methods: {
    updateCustomer(customer) {
      this.billToCustomer = { ...customer };
    },
    getDispatcherEmails(dispatcherId) {
      const dispatcher = this.getDispatcher(dispatcherId);

      if (!dispatcher) {
        return [];
      }

      return [...new Set([dispatcher.email, dispatcher.opsEmail].filter(Boolean))];
    },
    shouldAutofillSendUpdates() {
      const sendUpdatesTo = this.order.sendUpdatesTo;
      const isPreviousAutofilled =
        sendUpdatesTo.length === this.autofilledEmails.length &&
        sendUpdatesTo.every((email, index) => email === this.autofilledEmails[index]);

      const isEmpty = !sendUpdatesTo.length;

      // we can autofill send updates when it is empty or autofilled by previous dispatcher
      return isEmpty || isPreviousAutofilled;
    },
    updateOrderSendUpdatesTo(emails) {
      this.order.sendUpdatesTo = emails.flatMap((email) => email.value);
    },
  },
};
</script>

<template>
  <form-block>
    <div class="caption text-grey-darken-1 mb-4">Load information</div>
    <div class="mb-5">
      <v-row>
        <v-col cols="4">
          <dispatcher-select
            v-model="order.bookedBy"
            :rules="[requiredValidator]"
            :show-roles="[Role.booking]"
            data-qa="booked_by"
            label="Booked by"
            required
          ></dispatcher-select>
        </v-col>
        <v-col cols="8">
          <customer-select
            v-model="order.billToCustomerId"
            :rules="[requiredValidator]"
            data-qa="bill_to_customer_id"
            editable
            label="Bill to company"
            required
            @update="updateCustomer"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col lg="4" md="4" xl="4">
          <order-ref-number-input v-model="order.refNumbers" data-qa="ref_numbers"></order-ref-number-input>
        </v-col>
        <v-col lg="4" md="4" xl="4">
          <emails-field
            v-model="emails"
            :rules="[arrayRequiredValidator(emails)]"
            data-qa="send_updates_to"
            @update:model-value="updateOrderSendUpdatesTo"
          ></emails-field>
        </v-col>
        <v-col lg="4" md="4" xl="4">
          <v-text-field
            v-model="order.messageId"
            :label="$t('orders.message-id')"
            autocomplete="off"
            color="primary"
            data-qa="message_id"
            density="compact"
            hide-details="auto"
            maxlength="255"
            variant="outlined"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col lg="6" md="12" xl="6">
          <order-payments v-model="order.orderPayments" :order-status="order.status"></order-payments>
        </v-col>
      </v-row>
    </div>
    <div class="mb-5">
      <omni-textarea v-model="order.generalNote" data-qa="general-note" label="General note" rows="4" />
    </div>
    <div class="additional-info">
      <div class="caption text-grey-darken-1 mb-4">Additional information</div>
      <v-row class="align-center">
        <v-col lg="3" md="12" xl="3">
          <v-combobox
            v-model="order.checkInAs"
            :items="checkInAsList"
            :rules="[requiredValidator]"
            autocomplete="off"
            class="required"
            color="primary"
            data-qa="check_in_as"
            density="compact"
            hide-details="auto"
            label="Check in as"
            variant="outlined"
          />
        </v-col>
        <v-col lg="3" md="12" xl="2">
          <omni-select
            v-model="order.requiredDriverCertificates"
            :items="certificates"
            :multiple="true"
            data-qa="required_driver_certificates"
            label="Required Certificates"
          ></omni-select>
        </v-col>
        <v-col lg="8" md="12" xl="5">
          <div class="d-flex align-center">
            <v-checkbox
              v-model="order.underTracking"
              append-icon="mdi-map-marker-outline"
              class="body-2 pa-0 ma-0 mr-4 d-flex align-center"
              color="primary"
              data-qa="under_tracking"
              density="compact"
              hide-details
              label="Under tracking"
            />
            <v-checkbox
              v-model="order.teamForBroker"
              append-icon="mdi-account-multiple"
              class="text--darken-1 body-2 pa-0 ma-0 mr-4 d-flex align-center"
              color="primary"
              data-qa="team_for_broker"
              density="compact"
              hide-details
              label="Team for broker"
            />
            <v-checkbox
              v-model="order.hazmat"
              class="pa-0 ma-0 mr-4 d-flex align-center"
              color="primary"
              data-qa="hazmat"
              density="compact"
              hide-details
              label="Hazmat commodity"
            />
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="py-0">
          <v-checkbox
            v-model="order.$hasTemperatureControl"
            class="pa-0 ma-0"
            color="primary"
            data-qa="temperature-control"
            density="compact"
            hide-details
            label="Temperature control"
          />
        </v-col>
      </v-row>
      <v-row v-if="order.$hasTemperatureControl">
        <v-col lg="3" md="12" xl="3">
          <order-temperature-range
            v-model="order.temperatures"
            :disabled="order.protectFromFreezing"
          ></order-temperature-range>
        </v-col>
        <v-col lg="3" md="12" xl="2">
          <v-checkbox
            v-model="order.protectFromFreezing"
            class="pa-0 ma-0"
            color="primary"
            data-qa="protect_from_freezing"
            density="compact"
            hide-details
            label="Protect from freezing"
          />
        </v-col>
      </v-row>
    </div>
  </form-block>
</template>

<style lang="scss" scoped>
.additional-info {
  .v-input__append-outer {
    margin-left: 4px;

    .v-input__icon {
      width: 20px;
      min-width: 20px;
      height: 20px;
    }
  }
}
</style>
